/**
 * M-accounting projects - Mike's workbook plus what a user entered.
 *
 * Its own store, as Valuation has its own: an M-accounting project is not an accounting
 * model and shares none of its fields. A project holds INPUTS only; the server recomputes
 * the workbook on every read, so what comes back is always the current fixed model.
 */

import { create } from 'zustand';
import { useAuthStore } from './auth';
import type {
  AnnualSummaryComputed,
  ForecastDistributionPayload,
  ModelSettings,
  SensitivityComputed,
  WhatIfComputed,
} from '../types/financial-model';
import type { BacktestMape, BacktestRow } from '../types/financial-model-v2';

/** A backtest: one filed year re-run as a forecast in the workbook, against its figures. */
export interface MBacktest {
  holdoutYear: number;
  rows: BacktestRow[];
  mapeByCategory: BacktestMape[];
  availableYears: number[];
  seededFrom: number;
  notes: string[];
}

export const BASE = '/api/m/projects';

export function headers(): Record<string, string> {
  const token = useAuthStore.getState().token;
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function ok<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let detail = `Request failed (${response.status})`;
    try {
      const body = await response.json();
      if (typeof body.detail === 'string') detail = body.detail;
    } catch {
      /* not JSON */
    }
    throw new Error(detail);
  }
  return (response.status === 204 ? undefined : response.json()) as Promise<T>;
}

export interface MCell {
  value: number | string | null;
  formula: string | null;
  input: boolean;
  fix: string | null;
}

export interface MRow {
  row: number;
  label: string;
  markers: string[];
  cells: Record<string, MCell>;
}

export interface MTab {
  name: string;
  sheet: string;
  /** `year` is the period's key (the year itself in an annual model); `label` how it is
   *  written - 2024, Q1 2024, Jan 2024. */
  columns: { col: string; year: number | null; label?: string | null }[];
  rows: MRow[];
}

/** What a Baseline model covers: its filed periods from `first`, then its forecast. */
export interface MLayout {
  periodType: 'annual' | 'quarterly' | 'monthly';
  first: number;
  historical: number;
  forecast: number;
}

/** A new project's shape. Left out, the template: three filed years, two forecast, annual. */
export interface MShape {
  periodType?: MLayout['periodType'];
  firstYear?: number;
  filedYears?: number;
  forecastYears?: number;
}

export const PERIODS_PER_YEAR: Record<MLayout['periodType'], number> = { annual: 1, quarterly: 4, monthly: 12 };
/** The longest forecast either accounting model runs. */
export const MAX_FORECAST_YEARS = 5;

/** A period's label - Q1 2024 - where the computed model gives one; else the key itself. */
export function periodLabel(computed: MComputed | null | undefined, key: number | string): string {
  return computed?.labels?.[String(key)] ?? String(key);
}

export interface MComputed {
  historical: number[];
  forecast: number[];
  layout?: MLayout;
  labels?: Record<string, string>;
  /** Each calendar year's last period: where a year's balances are read. */
  yearEnds?: Record<string, number>;
  tabs: MTab[];
  /** A string is a spreadsheet error (#CYCLE!, #DIV/0!) - never read it as balanced. */
  checks: { balanceCheck: Record<string, number | string>; cashVsAccounts: Record<string, number | string> };
  annualSummary: AnnualSummaryComputed;
  /** The series IAS accounting's charts plot, keyed as IAS keys its periods (FY_2024, Q1_2024). */
  chartSeries?: Record<string, Record<string, number>>;
  whatIf: WhatIfComputed;
  sensitivity: SensitivityComputed;
  /** The fields the shared What-If and Sensitivity views read. */
  settings: Pick<ModelSettings, 'forecastStartYear' | 'numForecastYears' | 'periodType' | 'whatIfConfig' | 'sensitivityConfig'> & {
    historicalYears: number[];
  };
}

export interface MProjectSummary {
  id: string;
  name: string;
  description: string;
  version: number;
  inputCount: number;
  createdAt: string;
  updatedAt: string;
  /** Set on a project an import made: the file, and how Mike's model compared. */
  source?: MSource | null;
}

/** One figure, for one filed year: Mike's model against Accounting's on the same reading. */
export interface MAgreementRow {
  year: number;
  line: string;
  mike: number;
  accounting: number;
  difference: number;
}

export interface MSource {
  kind: 'pdf' | 'workbook' | 'sample' | 'companies-house' | 'companies-house-tags';
  filename: string;
  importedAt: string;
  notes: string[];
  readingWarnings: string[];
  agreement: MAgreementRow[];
  /** Where IAS accounting's reading disagrees with what the filing prints. */
  filingChecks?: { line: string; year: number; read: number; printed: number }[];
  /** Year -> how far the reading itself is out of balance. The baseline model balances
   * regardless, by carrying exactly this in its cash - so it is shown, not hidden. */
  readingImbalance?: Record<string, number>;
}

export interface MInput {
  sheet: string;
  cell: string;
  value: number | string | null;
}

/** One documented fix: what it corrects, why, and every cell it changes. */
export interface MFix {
  id: string;
  title: string;
  why: string;
  /** True when an entered figure replaces the fix's formula. */
  default: boolean;
  changes: { sheet: string; cell: string; was: unknown; now: unknown }[];
}

export interface MVersionRow {
  version: number;
  savedAt: string;
  label: string;
  inputCount: number;
  current: boolean;
}

interface ProjectResponse {
  project: MProjectSummary;
  inputs: MInput[];
  computed: MComputed;
}

interface MAccountingState {
  list: MProjectSummary[];
  project: MProjectSummary | null;
  inputs: MInput[];
  computed: MComputed | null;
  activeTab: string;
  isLoading: boolean;
  isSaving: boolean;
  isDirty: boolean;
  error: string | null;
  /** What an import in progress is doing; null when none is. */
  importProgress: string | null;
  /** Which import is running: a PDF shows IAS accounting's progress panel; a workbook takes a second. */
  importKind: 'pdf' | 'workbook' | null;
  /** A workbook in the template's layout, read as Accounting reads one. */
  importWorkbook: (file: File) => Promise<void>;
  /** A filing, read by Accounting's PDF reader in the background and polled. */
  importPdf: (file: File, options?: { reread?: boolean; forecastYears?: number }) => Promise<void>;
  /** A Companies House filing, fetched by the server and read as IAS accounting reads it -
   *  its tagged figures where filed, else its PDF - then placed in the baseline cells. */
  importFromCompaniesHouse: (
    number: string,
    transactionId: string,
    name: string,
    options?: { reread?: boolean },
  ) => Promise<void>;
  loadList: () => Promise<void>;
  create: (name: string, shape?: MShape) => Promise<string | null>;
  /** How many years the forecast runs, 1 to 5. Figures in a dropped forecast period go too. */
  setForecastYears: (years: number) => Promise<void>;
  /** Annual, quarterly or monthly, the figures moving with it; into shorter periods each
   *  year's amounts go in its last period or are spread evenly. */
  setPeriodType: (periodType: MLayout['periodType'], method: 'third_month' | 'equal_spread') => Promise<void>;
  open: (id: string) => Promise<void>;
  close: () => void;
  remove: (id: string) => Promise<void>;
  setActiveTab: (tab: string) => void;
  /** One cell. Saved on a short debounce, as v2 saves its model. */
  setInput: (sheet: string, cell: string, value: number | null) => void;
  saveNow: () => Promise<void>;
  updateConfig: (key: 'whatIfConfig' | 'sensitivityConfig', value: unknown) => Promise<void>;
  versions: () => Promise<MVersionRow[]>;
  restore: (version: number) => Promise<void>;
  dismissError: () => void;
  /** Every fix applied to the source workbook - fetched once, for the formulas panel. */
  fixes: MFix[] | null;
  loadFixes: () => Promise<void>;
  /** The open project as a workbook: the source workbook, corrected, with its inputs. */
  exportProject: () => Promise<void>;
  /** Baseline Accounting's template: the source workbook, corrected, as a blank form. */
  downloadTemplate: () => Promise<void>;

  // ── AI Forecast - IAS accounting's pipeline, on the workbook ──
  forecast: ForecastDistributionPayload | null;
  isForecasting: boolean;
  forecastProgress: string;
  forecastError: string | null;
  /** The last forecast, and any still running - read when a project opens. */
  loadForecast: () => Promise<void>;
  runForecast: (regenerate: boolean) => Promise<void>;
  /** Writes the forecast into the forecast columns, as a new version. */
  applyForecast: () => Promise<string[] | null>;

  // ── Backtest ──
  backtestYears: number[];
  backtest: MBacktest | null;
  isBacktesting: boolean;
  backtestError: string | null;
  backtestAnalysis: string | null;
  isAnalysingBacktest: boolean;
  loadBacktestYears: () => Promise<void>;
  runBacktest: (year: number) => Promise<void>;
  analyseBacktest: () => Promise<void>;
}

let saveTimer: ReturnType<typeof setTimeout> | null = null;

/** Save a response's body as a file, under the name given. */
async function download(response: Response, filename: string): Promise<void> {
  if (!response.ok) throw new Error(`Download failed (${response.status})`);
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

/** A file as base64, without the data: URL prefix the API does not want. */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result);
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(new Error('Could not read the file'));
    reader.readAsDataURL(file);
  });
}

/** As Accounting polls its own: generous, because a scanned filing takes minutes, but capped. */
const POLL_MS = 2000;
const MAX_POLLS = 450;

export const useMAccountingStore = create<MAccountingState>((set, get) => {
  /** Follow a running forecast until it finishes - on the project, so it survives a tab change. */
  const poll = async (projectId: string): Promise<void> => {
    set({ isForecasting: true });
    for (let i = 0; i < MAX_POLLS; i += 1) {
      await new Promise((resolve) => setTimeout(resolve, POLL_MS));
      if (get().project?.id !== projectId) return; // another project was opened
      const status = await ok<{ status: string; progress: string; error: string | null; distribution: ForecastDistributionPayload | null }>(
        await fetch(`${BASE}/${projectId}/forecast-status`, { headers: headers() }),
      );
      if (status.status === 'running') {
        set({ forecastProgress: status.progress });
        continue;
      }
      set({
        isForecasting: false,
        forecastProgress: '',
        forecast: status.distribution,
        forecastError: status.status === 'error' ? status.error : null,
      });
      return;
    }
    set({ isForecasting: false, forecastError: 'The forecast is taking longer than expected. Try again.' });
  };

  const accept = (body: ProjectResponse) =>
    set({ project: body.project, inputs: body.inputs, computed: body.computed, error: null });

  const fail = (error: unknown) =>
    set({ error: error instanceof Error ? error.message : String(error) });

  /** Follow a reading task - an upload's or a Companies House fetch's - to its project. */
  const followPdfImport = async (taskId: string) => {
    for (let i = 0; i < MAX_POLLS; i += 1) {
      await new Promise((resolve) => setTimeout(resolve, POLL_MS));
      const task = await ok<{ status: string; progress: string; projectId: string | null; error: string | null }>(
        await fetch(`${BASE}/import-pdf/${taskId}`, { headers: headers() }),
      );
      if (task.status === 'failed') throw new Error(task.error ?? 'The import failed.');
      if (task.status === 'complete' && task.projectId) {
        await get().open(task.projectId);
        void get().loadList();
        return;
      }
      set({ importProgress: task.progress || 'Working…' });
    }
    throw new Error('The import is taking longer than expected. Please try again.');
  };

  return {
    list: [],
    project: null,
    inputs: [],
    computed: null,
    activeTab: 'm-overview',
    isLoading: false,
    isSaving: false,
    isDirty: false,
    error: null,
    importProgress: null,
    importKind: null,
    fixes: null,
    forecast: null,
    isForecasting: false,
    forecastProgress: '',
    forecastError: null,
    backtestYears: [],
    backtest: null,
    isBacktesting: false,
    backtestError: null,
    backtestAnalysis: null,
    isAnalysingBacktest: false,

    loadForecast: async () => {
      const project = get().project;
      if (!project) return;
      try {
        const status = await ok<{ status: string; progress: string; error: string | null; distribution: ForecastDistributionPayload | null }>(
          await fetch(`${BASE}/${project.id}/forecast-status`, { headers: headers() }),
        );
        set({ forecast: status.distribution, forecastError: status.status === 'error' ? status.error : null });
        // A forecast started earlier and still running carries on being watched.
        if (status.status === 'running') void poll(project.id);
      } catch {
        /* a project with no forecast has nothing to show - not an error */
      }
    },

    runForecast: async (regenerate) => {
      const project = get().project;
      if (!project) return;
      set({ isForecasting: true, forecastError: null, forecastProgress: 'Starting…' });
      try {
        await ok(await fetch(`${BASE}/${project.id}/forecast/start?regenerate=${regenerate}`, {
          method: 'POST', headers: headers(),
        }));
        await poll(project.id);
      } catch (error) {
        set({ isForecasting: false, forecastError: error instanceof Error ? error.message : String(error) });
      }
    },

    applyForecast: async () => {
      const project = get().project;
      if (!project) return null;
      try {
        const body = await ok<ProjectResponse & { applied: { cells: number; notes: string[] } }>(
          await fetch(`${BASE}/${project.id}/forecast/apply`, { method: 'POST', headers: headers() }),
        );
        accept(body);
        return body.applied.notes;
      } catch (error) {
        set({ forecastError: error instanceof Error ? error.message : String(error) });
        return null;
      }
    },

    loadBacktestYears: async () => {
      const project = get().project;
      if (!project) return;
      try {
        const years = await ok<number[]>(await fetch(`${BASE}/${project.id}/backtest/years`, { headers: headers() }));
        set({ backtestYears: years });
      } catch (error) {
        set({ backtestError: error instanceof Error ? error.message : String(error) });
      }
    },

    runBacktest: async (year) => {
      const project = get().project;
      if (!project) return;
      set({ isBacktesting: true, backtestError: null, backtestAnalysis: null });
      try {
        const result = await ok<MBacktest>(await fetch(`${BASE}/${project.id}/backtest?year=${year}`, {
          method: 'POST', headers: headers(),
        }));
        set({ backtest: result });
      } catch (error) {
        set({ backtestError: error instanceof Error ? error.message : String(error) });
      } finally {
        set({ isBacktesting: false });
      }
    },

    analyseBacktest: async () => {
      const { project, backtest } = get();
      if (!project || !backtest) return;
      set({ isAnalysingBacktest: true });
      try {
        const body = await ok<{ analysis: string | null; generated: boolean }>(
          await fetch(`${BASE}/${project.id}/backtest/analyze?year=${backtest.holdoutYear}`, {
            method: 'POST', headers: headers(),
          }),
        );
        set({
          backtestAnalysis: body.analysis
            ?? 'No AI reading is available on this deployment - the backtest above stands on its own.',
        });
      } catch (error) {
        set({ backtestError: error instanceof Error ? error.message : String(error) });
      } finally {
        set({ isAnalysingBacktest: false });
      }
    },

    loadFixes: async () => {
      if (get().fixes) return;
      try {
        const fixes = await ok<MFix[]>(await fetch('/api/m/fixes', { headers: headers() }));
        set({ fixes });
      } catch (error) {
        fail(error);
      }
    },

    exportProject: async () => {
      const project = get().project;
      if (!project) return;
      try {
        // Unsaved edits first: the export is built from what the server holds.
        if (get().isDirty) await get().saveNow();
        await download(
          await fetch(`${BASE}/${project.id}/export`, { headers: headers() }),
          `${project.name} - baseline.xlsx`,
        );
      } catch (error) {
        fail(error);
      }
    },

    downloadTemplate: async () => {
      try {
        await download(
          await fetch('/api/m/template', { headers: headers() }),
          'Baseline-accounting-template.xlsx',
        );
      } catch (error) {
        fail(error);
      }
    },

    importWorkbook: async (file) => {
      set({ importProgress: 'Reading the workbook…', importKind: 'workbook', error: null });
      try {
        const form = new FormData();
        form.append('file', file);
        const token = useAuthStore.getState().token;
        const body = await ok<ProjectResponse>(
          await fetch(`${BASE}/import`, {
            method: 'POST',
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            body: form,
          }),
        );
        accept(body);
        set({ activeTab: 'm-overview', isDirty: false });
        void get().loadList();
      } catch (e) {
        fail(e);
      } finally {
        set({ importProgress: null, importKind: null });
      }
    },

    importPdf: async (file, options) => {
      set({ importProgress: 'Uploading…', importKind: 'pdf', error: null });
      try {
        const started = await ok<{ taskId: string }>(
          await fetch(`${BASE}/import-pdf`, {
            method: 'POST',
            headers: headers(),
            body: JSON.stringify({
              filename: file.name,
              file: await fileToBase64(file),
              reread: options?.reread ?? false,
              forecastYears: options?.forecastYears ?? 2,
            }),
          }),
        );
        await followPdfImport(started.taskId);
      } catch (e) {
        fail(e);
      } finally {
        set({ importProgress: null, importKind: null });
      }
    },

    importFromCompaniesHouse: async (number, transactionId, name, options) => {
      set({ importProgress: 'Fetching from Companies House…', importKind: 'pdf', error: null });
      try {
        const started = await ok<{ taskId: string }>(
          await fetch(`/api/v2/companies-house/company/${encodeURIComponent(number)}/import`, {
            method: 'POST',
            headers: headers(),
            body: JSON.stringify({ transactionId, name, reread: options?.reread ?? false, discipline: 'baseline' }),
          }),
        );
        await followPdfImport(started.taskId);
      } catch (e) {
        fail(e);
      } finally {
        set({ importProgress: null, importKind: null });
      }
    },

    loadList: async () => {
      set({ isLoading: true });
      try {
        set({ list: await ok<MProjectSummary[]>(await fetch(BASE, { headers: headers() })) });
      } catch (e) {
        fail(e);
      } finally {
        set({ isLoading: false });
      }
    },

    create: async (name, shape) => {
      try {
        const body = await ok<ProjectResponse>(
          await fetch(BASE, { method: 'POST', headers: headers(), body: JSON.stringify({ name, ...(shape ?? {}) }) }),
        );
        accept(body);
        set({ activeTab: 'm-overview' });
        return body.project.id;
      } catch (e) {
        fail(e);
        return null;
      }
    },

    open: async (id) => {
      set({ isLoading: true });
      try {
        accept(await ok<ProjectResponse>(await fetch(`${BASE}/${id}`, { headers: headers() })));
        // Another project's forecast and backtest are not this one's.
        set({
          activeTab: 'm-overview', isDirty: false,
          forecast: null, forecastError: null, isForecasting: false,
          backtest: null, backtestYears: [], backtestAnalysis: null, backtestError: null,
        });
        void get().loadForecast();
      } catch (e) {
        fail(e);
      } finally {
        set({ isLoading: false });
      }
    },

    close: () => {
      if (saveTimer) clearTimeout(saveTimer);
      set({ project: null, inputs: [], computed: null, isDirty: false });
      void get().loadList();
    },

    remove: async (id) => {
      try {
        await ok(await fetch(`${BASE}/${id}`, { method: 'DELETE', headers: headers() }));
        set({ list: get().list.filter((p) => p.id !== id) });
      } catch (e) {
        fail(e);
      }
    },

    setActiveTab: (tab) => set({ activeTab: tab }),

    setInput: (sheet, cell, value) => {
      const rest = get().inputs.filter((i) => !(i.sheet === sheet && i.cell === cell));
      // A cleared cell goes back to the template's own value, so it leaves the list.
      const inputs = value === null ? rest : [...rest, { sheet, cell, value }];
      set({ inputs, isDirty: true });
      if (saveTimer) clearTimeout(saveTimer);
      saveTimer = setTimeout(() => void get().saveNow(), 600);
    },

    saveNow: async () => {
      const { project, inputs } = get();
      if (!project) return;
      if (saveTimer) clearTimeout(saveTimer);
      set({ isSaving: true });
      try {
        accept(await ok<ProjectResponse>(await fetch(`${BASE}/${project.id}`, {
          method: 'PATCH', headers: headers(), body: JSON.stringify({ inputs }),
        })));
        set({ isDirty: false });
      } catch (e) {
        fail(e);
      } finally {
        set({ isSaving: false });
      }
    },

    setPeriodType: async (periodType, method) => {
      const { project } = get();
      if (!project) return;
      set({ isSaving: true });
      try {
        accept(await ok<ProjectResponse>(await fetch(`${BASE}/${project.id}`, {
          method: 'PATCH', headers: headers(), body: JSON.stringify({ periodType, method }),
        })));
      } catch (e) {
        fail(e);
      } finally {
        set({ isSaving: false });
      }
    },

    setForecastYears: async (years) => {
      const { project } = get();
      if (!project) return;
      set({ isSaving: true });
      try {
        accept(await ok<ProjectResponse>(await fetch(`${BASE}/${project.id}`, {
          method: 'PATCH', headers: headers(), body: JSON.stringify({ forecastYears: years }),
        })));
      } catch (e) {
        fail(e);
      } finally {
        set({ isSaving: false });
      }
    },

    updateConfig: async (key, value) => {
      const { project } = get();
      if (!project) return;
      set({ isSaving: true });
      try {
        accept(await ok<ProjectResponse>(await fetch(`${BASE}/${project.id}`, {
          method: 'PATCH', headers: headers(), body: JSON.stringify({ [key]: value }),
        })));
      } catch (e) {
        fail(e);
      } finally {
        set({ isSaving: false });
      }
    },

    versions: async () => {
      const { project } = get();
      if (!project) return [];
      try {
        return await ok<MVersionRow[]>(await fetch(`${BASE}/${project.id}/versions`, { headers: headers() }));
      } catch (e) {
        fail(e);
        return [];
      }
    },

    restore: async (version) => {
      const { project } = get();
      if (!project) return;
      try {
        accept(await ok<ProjectResponse>(await fetch(`${BASE}/${project.id}/versions/${version}/restore`, {
          method: 'POST', headers: headers(),
        })));
      } catch (e) {
        fail(e);
      }
    },

    dismissError: () => set({ error: null }),
  };
});
